import React, { createContext, useContext, useEffect, useState } from 'react'
import api from '../lib/api'
import { useAuthStore } from '../store/authStore'

interface FeatureFlags {
  marketplace: Record<string, boolean>
  auth: {
    registration: boolean
    google_sso: boolean
  }
  admin: {
    ai_config: boolean
    users_analytics: boolean
  }
  modules: {
    ai_usage: boolean
    offer_management_ai: boolean
    offer_management_public: boolean
    allegro_ads?: boolean
    notifications?: boolean
    multipack_sync?: boolean
  }
  user: {
    ai_config: boolean
  }
  integrations?: {
    quackback?: boolean
    /** Public Quackback instance URL when configured (anonymous widget OK). */
    quackback_base_url?: string | null
    /** Stable Quackback board ids for widget deep-link (from system_config). */
    quackback_board_bug?: string | null
    quackback_board_idea?: string | null
  }
  integration?: {
    baselinker?: boolean
  }
}

interface FeatureFlagContextType {
  flags: FeatureFlags
  isLoading: boolean
  isMarketplaceEnabled: (marketplace: string) => boolean
  isRegistrationEnabled: () => boolean
  isGoogleSSOEnabled: () => boolean
  isAIConfigEnabled: () => boolean
  isUsersAnalyticsEnabled: () => boolean
  isAIUsageEnabled: () => boolean
  isUserAIConfigEnabled: () => boolean
  isOfferManagementAIEnabled: () => boolean
  isOfferManagementPublicEnabled: () => boolean
  isModuleFlagEnabled: (name: string) => boolean
  isNotificationsEnabled: () => boolean
  isMultipackSyncEnabled: () => boolean
  isQuackbackConfigured: () => boolean
  getQuackbackBaseUrl: () => string | null
  getQuackbackBoardIds: () => { bug: string | null; idea: string | null }
  isIntegrationEnabled: (name: string) => boolean
  refresh: () => Promise<void>
}

const defaultFlags: FeatureFlags = {
  marketplace: {
    allegro: true,
    decathlon: false,
    castorama: false,
    leroymerlin: false,
    // Local/dev: show eMAG even if /config/feature-flags is unreachable (BE restart).
    // Prod/staging still get false until the API responds with the real flag.
    emag: Boolean(import.meta.env.DEV),
  },
  auth: {
    registration: true,
    google_sso: false,
  },
  admin: {
    ai_config: true,
    users_analytics: true,
  },
  modules: {
    ai_usage: false,
    offer_management_ai: false,
    offer_management_public: false,
    allegro_ads: false,
    // Off until /config/feature-flags says otherwise (admin or public launch).
    notifications: false,
    multipack_sync: false,
  },
  user: {
    ai_config: false,
  },
  integrations: {
    quackback: false,
    quackback_base_url: null,
    quackback_board_bug: null,
    quackback_board_idea: null,
  },
  integration: {
    baselinker: Boolean(import.meta.env.DEV),
  },
}

const defaultContextValue: FeatureFlagContextType = {
  flags: defaultFlags,
  isLoading: false,
  isMarketplaceEnabled: (marketplace: string) => defaultFlags.marketplace[marketplace.toLowerCase()] ?? false,
  isRegistrationEnabled: () => defaultFlags.auth.registration,
  isGoogleSSOEnabled: () => defaultFlags.auth.google_sso,
  isAIConfigEnabled: () => defaultFlags.admin.ai_config,
  isUsersAnalyticsEnabled: () => defaultFlags.admin.users_analytics,
  isAIUsageEnabled: () => defaultFlags.modules.ai_usage,
  isUserAIConfigEnabled: () => defaultFlags.user.ai_config,
  isOfferManagementAIEnabled: () => defaultFlags.modules.offer_management_ai,
  isOfferManagementPublicEnabled: () => defaultFlags.modules.offer_management_public,
  isModuleFlagEnabled: (name: string) => Boolean(defaultFlags.modules[name as keyof FeatureFlags['modules']]),
  isNotificationsEnabled: () => defaultFlags.modules.notifications ?? false,
  isMultipackSyncEnabled: () => defaultFlags.modules.multipack_sync ?? false,
  isQuackbackConfigured: () => defaultFlags.integrations?.quackback ?? false,
  getQuackbackBaseUrl: () => defaultFlags.integrations?.quackback_base_url ?? null,
  getQuackbackBoardIds: () => ({ bug: null, idea: null }),
  isIntegrationEnabled: (name: string) => Boolean(defaultFlags.integration?.[name as keyof NonNullable<typeof defaultFlags.integration>]),
  refresh: async () => {},
}

const FeatureFlagContext = createContext<FeatureFlagContextType>(defaultContextValue)

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(defaultFlags)
  const [isLoading, setIsLoading] = useState(true)
  const userId = useAuthStore((s) => s.user?.id)

  const fetchFlags = async () => {
    try {
      const response = await api.get<FeatureFlags>('/config/feature-flags')
      setFlags(response.data)
    } catch (error) {
      console.error('Failed to fetch feature flags:', error)
      // Keep whatever we already had (don't wipe eMAG/etc. on a transient BE blip).
      setFlags((prev) => prev)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setIsLoading(true)
    void fetchFlags()
  }, [userId])

  const isMarketplaceEnabled = (marketplace: string): boolean => {
    return flags.marketplace[marketplace.toLowerCase()] ?? false
  }

  const isRegistrationEnabled = (): boolean => flags.auth.registration
  const isGoogleSSOEnabled = (): boolean => flags.auth.google_sso
  const isAIConfigEnabled = (): boolean => flags.admin.ai_config
  const isUsersAnalyticsEnabled = (): boolean => flags.admin.users_analytics
  const isAIUsageEnabled = (): boolean => flags.modules.ai_usage
  const isUserAIConfigEnabled = (): boolean => flags.user.ai_config
  const isOfferManagementAIEnabled = (): boolean => flags.modules.offer_management_ai
  const isOfferManagementPublicEnabled = (): boolean => flags.modules.offer_management_public ?? false
  const isModuleFlagEnabled = (name: string): boolean =>
    Boolean(flags.modules[name as keyof FeatureFlags['modules']])
  const isNotificationsEnabled = (): boolean => flags.modules.notifications ?? false
  const isMultipackSyncEnabled = (): boolean => flags.modules.multipack_sync ?? false
  const isQuackbackConfigured = (): boolean => flags.integrations?.quackback ?? false
  const getQuackbackBaseUrl = (): string | null => {
    const url = flags.integrations?.quackback_base_url
    return typeof url === 'string' && url.trim() ? url.replace(/\/$/, '') : null
  }

  const getQuackbackBoardIds = (): { bug: string | null; idea: string | null } => {
    const bug = flags.integrations?.quackback_board_bug
    const idea = flags.integrations?.quackback_board_idea
    return {
      bug: typeof bug === 'string' && bug.trim() ? bug.trim() : null,
      idea: typeof idea === 'string' && idea.trim() ? idea.trim() : null,
    }
  }

  const isIntegrationEnabled = (name: string): boolean => {
    return Boolean(flags.integration?.[name as keyof NonNullable<FeatureFlags['integration']>])
  }

  const refresh = async () => {
    setIsLoading(true)
    await fetchFlags()
  }

  return (
    <FeatureFlagContext.Provider
      value={{
        flags,
        isLoading,
        isMarketplaceEnabled,
        isRegistrationEnabled,
        isGoogleSSOEnabled,
        isAIConfigEnabled,
        isUsersAnalyticsEnabled,
        isAIUsageEnabled,
        isUserAIConfigEnabled,
        isOfferManagementAIEnabled,
        isOfferManagementPublicEnabled,
        isModuleFlagEnabled,
        isNotificationsEnabled,
        isMultipackSyncEnabled,
        isQuackbackConfigured,
        getQuackbackBaseUrl,
        getQuackbackBoardIds,
        isIntegrationEnabled,
        refresh,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  )
}

export const useFeatureFlags = (): FeatureFlagContextType => {
  return useContext(FeatureFlagContext)
}
